import { useContainer } from "@contexts/container-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HttpStatusCode } from "axios";

import {
  trackSetupChecklistAttestationCleared,
  trackSetupChecklistAttestationRecorded,
  trackSetupChecklistQuestionAnswered,
} from "@lib/event-trackers";
import { isTransient, retryTransient } from "@lib/react-query/retry";

export const getSetupChecklistQueryKey = (orgCode) => [
  "setupChecklist",
  orgCode,
];

const STALE_VIEW = new Set([
  HttpStatusCode.Forbidden,
  HttpStatusCode.NotFound,
  HttpStatusCode.Conflict,
  HttpStatusCode.UnprocessableEntity,
]);

export const isStaleView = (error) => STALE_VIEW.has(error?.status);

// Action surfaces change the checklist without invalidating it, so every mount
// refetches.
export const useSetupChecklist = (orgCode, options = {}) => {
  const { setupChecklistService } = useContainer();

  return useQuery({
    queryKey: getSetupChecklistQueryKey(orgCode),
    queryFn: () => setupChecklistService.getChecklist(orgCode),
    enabled: !!orgCode,
    staleTime: 0,
    retry: retryTransient,
    ...options,
  });
};

// A write returns only its own row; the checklist is re-evaluated by
// refetching, and the mutation stays pending until that refetch settles. A 403,
// 404, 409 or 422 means the view the write was made from is stale, and a
// transient failure may have landed server-side, so all of them refetch the
// same way before reporting the error. Every write is idempotent, so a
// transient failure is retried first.
// Writes to one org share a scope and run in series, so one write's refetch
// never cancels another's.
const useChecklistMutation = (orgCode, { mutationFn, track }, options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...rest } = options;
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getSetupChecklistQueryKey(orgCode),
    });

  return useMutation({
    mutationFn,
    scope: { id: getSetupChecklistQueryKey(orgCode).join(":") },
    retry: retryTransient,
    onSuccess: (...args) => {
      track(args[1]);
      return Promise.all([invalidate(), onSuccess?.(...args)]);
    },
    onError: async (...args) => {
      const [error] = args;
      if (isStaleView(error) || isTransient(error)) await invalidate();
      return onError?.(...args);
    },
    ...rest,
  });
};

export const useConfirmAttestation = (orgCode, options = {}) => {
  const { setupChecklistService } = useContainer();

  return useChecklistMutation(
    orgCode,
    {
      mutationFn: (key) => setupChecklistService.confirm(orgCode, key),
      track: (key) => trackSetupChecklistAttestationRecorded(orgCode, key),
    },
    options
  );
};

export const useClearAttestation = (orgCode, options = {}) => {
  const { setupChecklistService } = useContainer();

  return useChecklistMutation(
    orgCode,
    {
      mutationFn: (key) => setupChecklistService.clear(orgCode, key),
      track: (key) => trackSetupChecklistAttestationCleared(orgCode, key),
    },
    options
  );
};

export const useAnswerQuestion = (orgCode, options = {}) => {
  const { setupChecklistService } = useContainer();

  return useChecklistMutation(
    orgCode,
    {
      mutationFn: ({ key, option }) =>
        setupChecklistService.answer(orgCode, key, option),
      track: ({ key, option }) =>
        trackSetupChecklistQuestionAnswered(orgCode, key, option),
    },
    options
  );
};
